// ส่งคะแนนสอบเข้าช่องในตารางคะแนนของห้องนี้
//
// ส่งผ่าน teachingModel.saveScoreMarks ตัวเดียวกับหน้าตารางคะแนน (ได้ audit / ยอดเกรด / ธง ร)
// ตารางคะแนนไม่ตามเฉลยที่แก้ทีหลังเอง — หน้านี้จึงบอกว่ามีกี่คนที่คะแนนในตารางไม่ตรงกับผลสอบตอนนี้
import React, { useMemo, useState } from 'react'
import { Alert, Button, Form, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { API, fmt, roomLabel, showError } from './examUtils'
import { toThaiDateLong } from '../utils/thaiDate'

const inactive = (s) => String(s.st_status) === '0' || s.transfer_room_id != null

export default function ExamPush({ examId, room, students, totalPoints, onPushed }) {
  const { setLoading } = useLoading()
  const items = room.score_items
  // ไม่เดาช่องให้ — ส่งผิดช่องแล้วคะแนนเดิมของช่องนั้นถูกทับทั้งห้อง ครูต้องเลือกเอง
  // ยกเว้นห้องที่เคยส่งไปแล้ว ตั้งต้นที่ช่องเดิมเพื่อให้กดส่งใหม่หลังแก้เฉลยได้ทันที
  const [siId, setSiId] = useState(() => room.si_id ?? null)
  const [touched, setTouched] = useState(false)
  const slot = items.find((it) => it.si_id === Number(siId)) || null

  const plan = useMemo(() => {
    const active = students.filter((s) => !inactive(s))
    const withSheet = active.filter((s) => s.score)
    const ready = withSheet.filter((s) => s.score.complete)
    const notReady = withSheet.filter((s) => !s.score.complete)
    return { active, ready, notReady, noSheet: active.length - withSheet.length }
  }, [students])

  const convert = (total) => (slot && totalPoints > 0
    ? Math.min(slot.si_maxscore, Math.round(((total * slot.si_maxscore) / totalPoints) * 100) / 100)
    : null)

  // เทียบกับช่องที่เคยส่งไปล่าสุดเท่านั้น (server อ่านคะแนนปัจจุบันของช่องนั้นมาให้)
  const stale = room.si_id && slot && room.si_id === slot.si_id
    ? plan.ready.filter((s) => s.slot_current !== s.slot_score)
    : []

  const push = async () => {
    if (!slot) {
      setTouched(true)
      return
    }
    const ok = await Swal.fire({
      icon: 'question',
      title: `ส่งคะแนนเข้าช่อง "${slot.si_name}"?`,
      html: `นักเรียน ${plan.ready.length} คน ห้อง ${roomLabel(room)}<br/>`
        + 'คะแนนเดิมในช่องนี้ของคนเหล่านี้จะถูกแทนที่ (บันทึกลงประวัติการแก้ไข)<br/>'
        + 'คนที่ไม่มีกระดาษคำตอบจะไม่ถูกแตะ',
      showCancelButton: true,
      confirmButtonText: 'ส่งคะแนน',
      cancelButtonText: 'ยกเลิก',
    })
    if (!ok.isConfirmed) return
    setLoading(true)
    try {
      const res = await axiosInstance.post(`${API}/exams/${examId}/rooms/${room.teach_id}/push`, { si_id: slot.si_id })
      await onPushed()
      Swal.fire({
        icon: res.data.skipped.length ? 'warning' : 'success',
        title: `ส่งคะแนนแล้ว ${res.data.pushed} คน`,
        html: res.data.skipped.length
          ? `ข้าม ${res.data.skipped.length} คน:<br/>${res.data.skipped.slice(0, 10).map((s) => `${s.st_name} — ${s.reason}`).join('<br/>')}`
          : undefined,
      })
    } catch (err) {
      showError(err, 'ส่งคะแนนไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  if (!items.length) {
    return (
      <Alert variant="warning" className="mb-0">
        ห้องนี้ยังไม่ได้ออกแบบตารางคะแนน — สร้างช่องคะแนนก่อน (เช่น "สอบกลางภาค 20 คะแนน") แล้วกลับมาส่งคะแนนที่นี่
        <div className="mt-2">
          <Button as={Link} to={`/teaching/score-table/${room.teach_id}`} size="sm" variant="warning">ไปที่ตารางคะแนน</Button>
        </div>
      </Alert>
    )
  }

  return (
    <>
      <Form.Group className="mb-3" style={{ maxWidth: 420 }}>
        <Form.Label>ช่องในตารางคะแนนของห้อง {roomLabel(room)}</Form.Label>
        <Form.Select value={siId ?? ''} isInvalid={touched && !slot}
          onChange={(e) => { setSiId(e.target.value ? Number(e.target.value) : null); setTouched(true) }}>
          <option value="">-- เลือกช่องคะแนน --</option>
          {items.map((it) => (
            <option key={it.si_id} value={it.si_id}>{it.si_name} (เต็ม {fmt(it.si_maxscore)})</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">เลือกช่องคะแนนที่จะส่งคะแนนสอบเข้าไปก่อน</Form.Control.Feedback>
      </Form.Group>

      {slot && (
        <Alert variant="light" className="border fs-sm">
          ข้อสอบเต็ม <b>{fmt(totalPoints)}</b> → ช่อง "{slot.si_name}" เต็ม <b>{fmt(slot.si_maxscore)}</b>
          {Number(totalPoints) !== Number(slot.si_maxscore)
            ? <> · แปลงตามสัดส่วน (คูณ {fmt(slot.si_maxscore / totalPoints, 4)}) ปัดทศนิยมสองตำแหน่ง</>
            : <> · ใช้คะแนนตรงตัว</>}
        </Alert>
      )}

      <ul className="fs-sm">
        <li>พร้อมส่ง <b>{plan.ready.length}</b> คน</li>
        {plan.notReady.length > 0 && <li className="text-warning">ยังตรวจไม่ครบ (อัตนัยยังไม่ได้ให้คะแนน หรือบางข้อยังไม่มีเฉลย) {plan.notReady.length} คน — จะถูกข้าม</li>}
        {plan.noSheet > 0 && <li className="text-secondary">ไม่มีกระดาษคำตอบ {plan.noSheet} คน — คะแนนเดิมในช่องนี้ไม่ถูกแตะ</li>}
      </ul>

      {room.pushed_at && (
        <div className="fs-sm text-secondary mb-2">
          ส่งล่าสุด {toThaiDateLong(room.pushed_at)}{room.pushed_by_name ? ` โดย ${room.pushed_by_name}` : ''}
        </div>
      )}
      {stale.length > 0 && (
        <Alert variant="warning" className="fs-sm py-2">
          คะแนนในตารางคะแนนไม่ตรงกับผลสอบตอนนี้ {stale.length} คน (แก้เฉลยหรือคำตอบหลังส่งไปแล้ว) — กดส่งใหม่เพื่ออัปเดต
        </Alert>
      )}

      <Button disabled={!plan.ready.length} onClick={push}>
        <i className="ri-upload-2-line"></i> ส่งคะแนน {plan.ready.length} คนเข้าตารางคะแนน
      </Button>
      <Button as={Link} to={`/teaching/score-table/${room.teach_id}`} variant="link" size="sm">เปิดตารางคะแนน</Button>

      {slot && plan.ready.length > 0 && (
        <div className="table-responsive mt-3" style={{ maxHeight: 400 }}>
          <Table size="sm" className="mb-0">
            <thead>
              <tr>
                <th style={{ width: 45 }}>เลขที่</th>
                <th>ชื่อ-สกุล</th>
                <th className="text-end">คะแนนสอบ</th>
                <th className="text-end">เข้าช่อง</th>
                {room.si_id === slot.si_id && <th className="text-end">ในตารางตอนนี้</th>}
              </tr>
            </thead>
            <tbody>
              {plan.ready.map((s) => {
                const next = convert(s.score.total)
                return (
                  <tr key={s.study_id}>
                    <td>{s.seat}</td>
                    <td>{s.st_name}</td>
                    <td className="text-end">{fmt(s.score.total)}</td>
                    <td className="text-end fw-semibold">{fmt(next)}</td>
                    {room.si_id === slot.si_id && (
                      <td className={`text-end ${s.slot_current !== next ? 'text-warning' : 'text-secondary'}`}>
                        {fmt(s.slot_current)}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      )}
    </>
  )
}
